import { BaseControl } from '@Common/Controls/BaseControl';
import { Control } from '@Common/Controls/Controls';
import { link } from '@Common/Controls/Link';
import { Button } from '@Common/Controls/SimpleButton';
import {
    Grid,
    GridButtonEditor,
    GridCell,
    GridRow,
    IExternalEditor,
    IGridColumnProperties,
    IGridProperties,
    RowCollection
} from '@Common/Controls/SimpleGridLight';
import { MulticastDelegateBag } from '@Common/Core/MulticastDelegate';
import { isPromiseLike } from '@Common/Helpers/CommonUtils';
import { enumerable } from '@Common/Core/CoreDecorator';
import { dataSource } from './SimpleGridLight';
import { safeDestroys } from '@Common/Core/Destroyable';

export interface IGridControlCESetting<TData = any> extends IGridProperties<TData> {
    canDelete?: boolean;
    canAdd?: boolean;
    canEdit?: boolean;
    addRowText?: string;
    deleteText?: string;
    data?: TData[];
    columns?: IGridColumnProperties<TData>[];
}

const gridControlCEDefaultSetting: IGridControlCESetting<any> = {
    canDelete: true,
    canAdd: true,
    canEdit: false,
    addRowText: strings.gridControlCE.newRowTitle,
    deleteText: 'X'
};

export interface IRowEventArg {
    row: GridRow;
}

export interface IBeforeAddRowEventArg {
    newData: any | ICallback<ICallback<any>> | Promise<any>;
}

export interface IBeforeDeleteRowEventArg extends IRowEventArg {
    cancel: boolean;
}

class NullControl implements Control {
    public getControl(): JQuery {
        return undefined;
    }

    public setState(enabled: boolean): void {}
}

export class GridControlCE<TData = any> extends BaseControl {
    protected readonly eventBag = new MulticastDelegateBag();
    public readonly onBeforeAddRow = this.eventBag.create<(arg: IBeforeAddRowEventArg) => void>();
    public readonly onBeforeDeleteRow = this.eventBag.create<(arg: IBeforeDeleteRowEventArg) => void>();
    public readonly onEditRow = this.eventBag.create<(rowArg: IRowEventArg) => void>();
    public readonly onChange = this.eventBag.create<IHandler>();

    private setting: IGridControlCESetting;
    private grid: Grid;
    private addButton: Control;
    private pinnedRows: GridRow[] = [];

    constructor(setting: IGridControlCESetting<TData>) {
        super();
        this.setting = $.extend({}, gridControlCEDefaultSetting, setting);
        this.init();
        this.grid.onChange(this.onChange);
    }

    @enumerable
    public get rows(): RowCollection<TData> {
        return this.getInnerGrid().rowsCollection;
    }

    private static createButtonColumn(
        className: string,
        tooltip: string,
        callBack: (cell: GridCell, button: Button) => IExternalEditor
    ): IGridColumnProperties {
        return {
            title: '',
            isSortable: false,
            width: { desired: 40, min: 40, max: 40 },
            editor: new GridButtonEditor({
                fn: callBack,
                buttonText: '',
                buttonSetting: {
                    className: `grid-command-btn ${className}`,
                    tooltip: tooltip,
                    backgroundColor: 'transparent',
                    toggleBackground: 'transparent',
                    minWidth: '0px'
                }
            }),
            cellCss: {
                textAlign: 'center'
            }
        };
    }

    public destroy(): void {
        safeDestroys(this.grid, this.eventBag);
        super.destroy();
    }

    public getInnerGrid(): Grid {
        return this.grid;
    }

    public deleteColumnHandler(cell: GridCell): null {
        const arg: IBeforeDeleteRowEventArg = { row: cell.getRow(), cancel: false };
        this.onBeforeDeleteRow(arg);
        if (!arg.cancel) {
            this.grid.rowsCollection.exitFromPreviewRow();
            cell.getRow().remove();
        }
        return null;
    }

    public appendData(data: any): void {
        this.grid.beginLoad();
        for (const row of this.grid.appendData(data)) {
            row.isPinned = true;
            this.pinnedRows.push(row);
        }
        this.grid.endLoad();
    }

    public disableAddButton(val: boolean): void {
        this.addButton.setState(!val);
    }

    public adjust(width?, height?): void {
        //Debuger.writeLn((<any>commonUtils).objectToString({ adjust: arguments }));
        this.grid.adjust(width, height);
    }

    public enabled(value?: boolean): boolean {
        return this.grid.enabled(value);
    }

    public setState(value?: boolean): void {
        this.grid.setState(value);
    }

    public getData(): TData[] {
        return this.grid.value();
    }

    public setData(data: TData[] | dataSource.IDataSource): void {
        this.grid.value(data);
    }

    public refresh(): void {
        this.grid.value(this.grid.value());
    }

    public getControl(): JQuery {
        return this.grid.getControl();
    }

    public onCellChanged(fn: (cell: GridCell) => void): void {
        this.grid.onCellChanged(fn);
    }

    public onValidate(fn: (state: boolean) => void): void {
        this.grid.onValidate(fn);
    }

    public readonly(isReadonly?: boolean): boolean {
        return this.grid.readonly(isReadonly);
    }

    public focus(): void {
        this.getInnerGrid().focus();
    }

    private init(): void {
        this.addButton = this.initAddButton(this.setting);
        if (this.setting.canEdit) {
            this.setting.columns.push(this.createEditColumn());
        }
        if (this.setting.canDelete) {
            this.setting.columns.push(this.createDeleteColumn());
        }
        this.setting.footer = {
            flexible: true,
            content: this.addButton.getControl(),
            css: {
                textAlign: 'left'
            }
        };

        this.grid = new Grid(this.setting);
        this.grid.onSorting(() => this.unpinRows());
        this.grid.onFiltering(() => this.unpinRows());
    }

    private unpinRows(): void {
        for (const row of this.pinnedRows) {
            row.isPinned = false;
        }
        this.pinnedRows = [];
    }

    private createDeleteColumn(): IGridColumnProperties {
        return GridControlCE.createButtonColumn('deleteRow', strings.gridControlCE.delete, this.deleteColumnHandler.bind(this));
    }

    private createEditColumn(): IGridColumnProperties {
        return GridControlCE.createButtonColumn('editRow', strings.gridControlCE.edit, (cell: GridCell) => {
            const arg: IBeforeDeleteRowEventArg = { row: cell.getRow(), cancel: false };
            this.onEditRow(arg);
            return null;
        });
    }

    private initAddButton(setting: IGridControlCESetting): Control {
        if (!setting.canAdd) {
            return new NullControl();
        }

        const addButton = link({
            width: '100%',
            text: setting.addRowText,
            className: 'gridControlCE-addNewRow'
        });

        addButton.click(() => {
            this.addRowButtonClick();
        });
        return addButton as any;
    }

    private readonly processNewData = _.cond([
        [_.isNil, _.noop],
        [_.isFunction, func => func(data => this.appendData(data))],
        [isPromiseLike, async promise => this.appendData(await promise)],
        [_.stubTrue, data => this.appendData(data)]
    ]);

    private addRowButtonClick(): void {
        const arg: IBeforeAddRowEventArg = {
            newData: null
        };
        this.onBeforeAddRow(arg);
        this.processNewData(arg.newData);
    }
}
